import { RectSize } from "../../../utils/RectSize";
import { AppSettings } from "../../managers/AppSettings";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { SettingsItem } from "../options/SettingsItem";
import { LabelIconButton } from "../../../ui/LabelIconButton";
import { Padding } from "../../../utils/Padding";
import { OptionsItemBase } from "../options/OptionsItemBase";
import { LayoutDataManager } from "../../managers/LayoutDataManager";


/**
 * Base class for ScrollPane 'content with title' for ScrollPane with multi-content support
 * @see ContentsScrollPane
 */
export class ScrollPaneTitledListContent
    extends Phaser.GameObjects.Container {
    /**
     * Title is icon + text on top of the whole content/category section (above table)
     */
    public btnTitle: LabelIconButton;
    protected titlePadding: Padding = new Padding(0, 0, 0, 30);

    /**
     * Header is top most table row
     */
    public header: SettingsItem;
    protected items: OptionsItemBase[];
    protected size: RectSize = new RectSize();
    public nsContentBg: Phaser.GameObjects.NineSlice;

    /**
     * Localization node for localization data for this component
     * - override in subclass
     */
    protected nodeLoc: any;
    /**
     * Data node for data for this component
     * - override in subclass
     */
    protected nodeTheme: any;

    protected titleHeightInclPadding: number;
    protected nsBorderInnerPadding: number = 30;


    /**
     * 
     * @param title Category/section/content localized title (on top of table)
     */
    public Init(): void {
        this.items = [];
    }

    /**
     * Build contnet for this section
     * - contains shared functionality, override for additional functionality.
     */
    public Build(): void {
        const nsContentBgBorderSize = 55;
        this.nsContentBg = this.scene.make.nineslice({
            key: LoaderFileKeys.ui,
            frame: "rect-black-w72-r24-A60-ds.png",
            leftWidth: nsContentBgBorderSize,
            rightWidth: nsContentBgBorderSize,
            topHeight: nsContentBgBorderSize,
            bottomHeight: nsContentBgBorderSize,
            // alpha: 0.9, // text already has an alpha?
        });
        this.nsContentBg.setOrigin(0);

        // refs
        // NOTE: some subclasses with poolable categories might choose 
        // not to set icon in Init. Those can later call @see UpdateIcon to do so.
        const sTitleIconFrame: string = this.nodeTheme?.title.iconFrame ?? "rect-2x2-transparent.png";
        const titleTextShadow: Phaser.Types.GameObjects.Text.TextShadow = {
            color: "#000000",
            fill: true,
            blur: 4,
            offsetX: 0,
            offsetY: 2,
        };

        // btnTitle
        // - section icon + name above the whole table
        this.btnTitle = new LabelIconButton(this.scene);
        this.btnTitle.InitBase({
            out: { texture: LoaderFileKeys.ui, frame: "rect-2x2-transparent.png" },
        }, false);
        this.btnTitle.InitLabel(
            // label states data
            {
                out: {
                    color: "#fff",
                    fontSize: "50px",
                    fontFamily: AppSettings.fontFamilies.MontserratBold,
                    shadow: titleTextShadow,
                    metrics: AppSettings.metrics.montserratBold50,
                },
            },
            // label offset
            new Phaser.Geom.Point(50, 0)
        );
        this.btnTitle.label.setOrigin(0, 0.5);
        this.btnTitle.InitIcon({
            out: { texture: LoaderFileKeys.ui, frame: sTitleIconFrame },
        });
        this.btnTitle.PostInit();
        this.add(this.btnTitle);

        // override in subclass
    }

    protected UpdateIcon() {
        this.btnTitle.icon.setFrame(this.nodeTheme.title.iconFrame);
    }

    public GetItems(): OptionsItemBase[] {
        return this.items;
    }

    public GetItemsLength(): number {
        return this.items.length;
    }

    public GetItemByIndex(itemIndex: number): OptionsItemBase {
        return this.items[itemIndex];
    }

    /**
     * @alike: OnResize
     * 
     * Set width->Get Size (width, height)
     * - Set width, calc height for each item separately 
     *   and height for whole content in totality
     * 
     * @param width  
     */
    public SetWidth(width: number): void {
        // btnTitle
        // - title above table
        const titleHeight = Math.max(this.btnTitle.label.displayHeight, this.btnTitle.icon.displayHeight) * this.btnTitle.scale;
        this.titleHeightInclPadding = this.titlePadding.top + titleHeight + this.titlePadding.bottom;

        // btnTitle
        this.btnTitle.setPosition(30, this.titlePadding.top + titleHeight / 2);
        this.size.Set(width, this.titleHeightInclPadding);

        // header
        // NOTE: some contents dont have header @see DealCompleteHandsContent
        if (this.header != undefined) {
            this.header.SetSize(width, this.header.SetWidthGetHeight(width));
            this.header.y = this.size.height;
            this.size.height += this.header.GetSize().height;
        }

        // items
        this.items.forEach((item: SettingsItem) => {
            item.SetSize(width, item.SetWidthGetHeight(width));
        });

        // reposition
        // const startDropItemsY = this.header.GetSize().height;
        // this.size.SetSize(width, startDropItemsY);
        this.items.forEach((item: SettingsItem, i) => {
            item.y = this.size.height;
            const itemSize: RectSize = item.GetSize();
            this.size.height += itemSize.height;
            // console.log(i, itemSize.height)
        });

        // override in subclass
        // TODO subclass: this.RedrawBorder
    }

    protected RedrawBorder(): void {
        // ref
        // const titleHeight = Math.max(this.btnTitle.label.displayHeight, this.btnTitle.icon.displayHeight) * this.btnTitle.scale;
        // const titleHeightInclPadding = this.titlePadding.top + titleHeight + this.titlePadding.bottom;

        // this.nsContentBg.y = this.titleHeightInclPadding - this.nsBorderInnerPadding;
        this.nsContentBg.setPosition(
            -this.nsBorderInnerPadding,
            this.titleHeightInclPadding - this.nsBorderInnerPadding,
        )
        this.nsContentBg.setSize(
            this.size.width + this.nsBorderInnerPadding * 2,
            this.size.height + this.nsBorderInnerPadding * 2 - this.titleHeightInclPadding //+ 10
        );
    }

    public GetSize(): RectSize {
        return this.size;
    }

    public UpdateFromLocalizationChange(): void {
        // override in subclass
    }
}